import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { strapiListSampleReceiveLogs, type SampleReceiveLogRow } from '../../../api/strapiCrm';

type Props = {
  open: boolean;
  workOrderDocumentId: string | null;
  workOrderLabel?: string | null;
  jwt: string | null;
  onClose: () => void;
};

function formatReceivedAt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('id-ID', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

function itemLabel(item: SampleReceiveLogRow['received_items'][number]): string {
  const parts = [item.point_label, item.jenis_pengukuran_label, item.location_name].filter((s) =>
    Boolean(s?.trim()),
  );
  return parts.join(' — ') || item.berita_acara_document_id || 'Titik';
}

function conditionLabels(log: SampleReceiveLogRow): string[] {
  const labels: string[] = [];
  if (log.preservation_by_customer) labels.push('Pengawetan pelanggan');
  if (log.preservation_by_laboratory) labels.push('Pengawetan laboratorium');
  if (log.received_condition_tidak_diawetkan_di_lapangan) labels.push('Tidak diawetkan di lapangan');
  if (log.received_condition_wadah_tidak_sesuai) labels.push('Wadah tidak sesuai');
  if (log.received_condition_kadaluarsa) labels.push('Sampel kedaluarsa');
  if (log.received_condition_lainnya) {
    labels.push(log.received_condition_note ? `Lainnya: ${log.received_condition_note}` : 'Lainnya');
  }
  return labels;
}

export function SamplePreservationReceiveLogsDialog({
  open,
  workOrderDocumentId,
  workOrderLabel,
  jwt,
  onClose,
}: Props) {
  const [logs, setLogs] = useState<SampleReceiveLogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !workOrderDocumentId || !jwt) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void strapiListSampleReceiveLogs(jwt, workOrderDocumentId)
      .then((rows) => {
        if (!cancelled) setLogs(rows);
      })
      .catch((e) => {
        if (!cancelled) {
          setLogs([]);
          setError(e instanceof Error ? e.message : 'Gagal memuat log penerimaan.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, workOrderDocumentId, jwt]);

  useEffect(() => {
    if (open) return;
    setLogs([]);
    setError(null);
    setLoading(false);
  }, [open]);

  const titleLabel = workOrderLabel?.trim() || workOrderDocumentId || 'work order';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid="sample-preservation-receive-logs-dialog"
    >
      <DialogTitle sx={{ pr: 6 }}>
        Log penerimaan sample — {titleLabel}
        <IconButton aria-label="Tutup" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : null}
        {!loading && !error && logs.length === 0 ? (
          <Alert severity="info">Belum ada log penerimaan untuk work order ini.</Alert>
        ) : null}
        {!loading && logs.length > 0 ? (
          <Stack spacing={2} divider={<Divider flexItem />}>
            {logs.map((log) => (
              <Box key={log.document_id} data-testid={`sample-preservation-receive-log-${log.document_id}`}>
                <Typography variant="subtitle2">{formatReceivedAt(log.received_at)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Diterima oleh {log.received_by_name || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Titik yang diterima
                </Typography>
                <Stack spacing={0.5} sx={{ mb: 1 }}>
                  {log.received_items.length ? (
                    log.received_items.map((item, idx) => (
                      <Typography key={`${item.berita_acara_document_id}-${idx}`} variant="body2">
                        {itemLabel(item)}
                      </Typography>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Stack>
                {conditionLabels(log).length ? (
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {conditionLabels(log).map((label) => (
                      <Chip key={label} size="small" label={label} />
                    ))}
                  </Stack>
                ) : null}
              </Box>
            ))}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Tutup</Button>
      </DialogActions>
    </Dialog>
  );
}
